import React, { useMemo, useState, useEffect } from 'react';
import { Translate } from 'react-localize-redux';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import { formatCurrency, stringDatePostgresToDate } from '../../../utils/index.jsx';
import { Grid, Paper, Typography, FormControl, Button, Card, Chip, Box } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { InsuranceBill } from './types';
import { useInsuranceBills } from '../../../hooks/useInsuranceBills';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

type AccountabilityTableProps = {
    investigationUuid: string;
    languageCode: string;
    currency: string;
    // For Storybook/testing - bypass hook if provided
    bills?: InsuranceBill[];
    loading?: boolean;
    error?: string | null;
};

interface OrganizationAccountability {
    organizationName: string;
    organizationUuid: string;
    billsCount: number;
    totalBilled: number;
    totalReimbursed: number;
    totalPending: number;
}

const AccountabilityTable: React.FC<AccountabilityTableProps> = ({
    investigationUuid,
    languageCode,
    currency,
    bills: providedBills,
    loading: providedLoading,
    error: providedError,
}) => {
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [currentPageTable, setCurrentPageTable] = useState<number>(0);

    // Build filters for API call
    const apiFilters = useMemo(() => {
        const filters: {
            periodStart?: Date;
            periodEnd?: Date;
        } = {};

        if (startDate) {
            filters.periodStart = new Date(startDate);
        }
        if (endDate) {
            filters.periodEnd = new Date(endDate);
        }

        return filters;
    }, [startDate, endDate]);

    // Use provided bills for Storybook/testing, otherwise use hook
    const hookResult = useInsuranceBills({
        investigationUuid,
        filters: apiFilters,
        enabled: !providedBills // Disable hook if bills are provided
    });

    const bills = providedBills || hookResult.bills;
    const loading = providedLoading !== undefined ? providedLoading : hookResult.loading;
    const error = providedError !== undefined ? providedError : hookResult.error;
    const refetch = hookResult.refetch;

    useEffect(() => {
        setCurrentPageTable(0);
    }, [startDate, endDate]);

    // Aggregate bills by organization
    const organizationData = useMemo(() => {
        const orgMap = new Map<string, OrganizationAccountability>();

        bills.forEach(bill => {
            const existing = orgMap.get(bill.organizationName) || {
                organizationName: bill.organizationName,
                organizationUuid: bill.organizationUuid,
                billsCount: 0,
                totalBilled: 0,
                totalReimbursed: 0,
                totalPending: 0
            };

            existing.billsCount++;
            existing.totalBilled += bill.totalGross;
            existing.totalReimbursed += bill.totalPaid;
            existing.totalPending = existing.totalBilled - existing.totalReimbursed;

            orgMap.set(bill.organizationName, existing);
        });

        return Array.from(orgMap.values());
    }, [bills]);

    // Calculate overall stats
    const overallStats = useMemo(() => {
        return {
            totalOrganizations: organizationData.length,
            totalBills: organizationData.reduce((sum, org) => sum + org.billsCount, 0),
            totalBilled: organizationData.reduce((sum, org) => sum + org.totalBilled, 0),
            totalReimbursed: organizationData.reduce((sum, org) => sum + org.totalReimbursed, 0),
            totalPending: organizationData.reduce((sum, org) => sum + org.totalPending, 0),
        };
    }, [organizationData]);

    // Render status chip based on pending amount
    const renderStatusChip = (totalPending: number) => {
        const hasPending = totalPending > 0;
        return (
            <Chip
                icon={hasPending ? <ErrorIcon /> : <CheckCircleIcon />}
                label={
                    <Translate id={hasPending ? "hospital.billing.accountability.status.pending" : "hospital.billing.accountability.status.paid"} />
                }
                style={{
                    backgroundColor: hasPending ? '#f44336' : '#4CAF50',
                    color: '#fff',
                    fontWeight: 'bold'
                }}
                size="small"
            />
        );
    };

    // Convert to table rows
    const tableRows = useMemo(() => {
        return organizationData.map((org, index) => ({
            id: index,
            organizationName: org.organizationName,
            billsCount: org.billsCount,
            totalBilled: formatCurrency(org.totalBilled, currency, languageCode),
            totalReimbursed: formatCurrency(org.totalReimbursed, currency, languageCode),
            totalPending: formatCurrency(org.totalPending, currency, languageCode),
            totalPendingRaw: org.totalPending,
            status: renderStatusChip(org.totalPending),
        }));
    }, [organizationData, currency, languageCode]);

    function changePageCallback(currentPage: number) {
        setCurrentPageTable(currentPage);
    }

    function renderCore() {
        if (loading) {
            return (
                <Paper style={{ padding: '2rem', textAlign: 'center' }}>
                    <Typography>
                        <Translate id="general.loading.loading" />
                    </Typography>
                </Paper>
            );
        }

        if (error) {
            return (
                <Paper style={{ padding: '2rem', textAlign: 'center' }}>
                    <Typography color="error">{error}</Typography>
                    <Button onClick={refetch} style={{ marginTop: '1rem' }}>
                        <Translate id="general.retry" />
                    </Button>
                </Paper>
            );
        }

        if (tableRows.length === 0) {
            return (
                <Paper style={{ padding: '2rem', textAlign: 'center' }}>
                    <Typography>
                        <Translate id="hospital.billing.accountability.no_data" />
                    </Typography>
                    {(startDate || endDate) && (
                        <Typography variant="body2" style={{ marginTop: '0.5rem', color: '#666' }}>
                            <Translate id="hospital.billing.accountability.try_different_dates" />
                        </Typography>
                    )}
                </Paper>
            );
        }

        const headCells = [
            {
                id: 'organizationName',
                alignment: 'left' as const,
                label: <Translate id="hospital.billing.accountability.organization" />
            },
            {
                id: 'billsCount',
                alignment: 'left' as const,
                label: <Translate id="hospital.billing.accountability.bills_count" />
            },
            {
                id: 'totalBilled',
                alignment: 'left' as const,
                label: [<Translate id="hospital.billing.accountability.total_billed" />, `(${currency})`]
            },
            {
                id: 'totalReimbursed',
                alignment: 'left' as const,
                label: [<Translate id="hospital.billing.accountability.total_reimbursed" />, `(${currency})`]
            },
            {
                id: 'totalPending',
                alignment: 'left' as const,
                label: [<Translate id="hospital.billing.accountability.total_pending" />, `(${currency})`]
            },
            {
                id: 'status',
                alignment: 'right' as const,
                label: <Translate id="hospital.billing.accountability.status.label" />
            }
        ];

        return (
            <EnhancedTable
                noHeader
                headCells={headCells}
                rows={tableRows}
                noSelectable
                currentPage={currentPageTable}
                changePageCallback={changePageCallback}
            />
        );
    }

    // Date filters component in a card
    const renderDateFilters = () => (
        <Card style={{ margin: '0 0 10px 0', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                {/* Start Date */}
                <FormControl variant="outlined" margin="dense" style={{ maxWidth: '220px' }}>
                    <DatePicker
                        label={<Translate id="hospital.search_box.start_date" />}
                        value={startDate ? dayjs(startDate) : null}
                        onChange={(newDate) => setStartDate(newDate ? newDate.format('YYYY-MM-DD') : '')}
                        slotProps={{
                            textField: {
                                variant: 'outlined',
                                fullWidth: true
                            }
                        }}
                    />
                </FormControl>
                {/* End Date */}
                <FormControl variant="outlined" margin="dense" style={{ maxWidth: '220px' }}>
                    <DatePicker
                        label={<Translate id="hospital.search_box.end_date" />}
                        value={endDate ? dayjs(endDate) : null}
                        onChange={(newDate) => setEndDate(newDate ? newDate.format('YYYY-MM-DD') : '')}
                        slotProps={{
                            textField: {
                                variant: 'outlined',
                                fullWidth: true
                            }
                        }}
                    />
                </FormControl>
                {/* Clear Dates Button */}
                {(startDate || endDate) && (
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={() => {
                            setStartDate('');
                            setEndDate('');
                        }}
                        style={{
                            height: '40px',
                            minWidth: '100px',
                            textTransform: 'none'
                        }}
                    >
                        <Translate id="hospital.search_box.clear_dates" />
                    </Button>
                )}
            </div>
        </Card>
    );

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                {renderDateFilters()}
            </Grid>

            {tableRows.length > 0 && (
                <Grid item xs={12}>
                    <Card style={{ margin: '0 0 10px 0' }}>
                        <Grid container justifyContent="center" padding={2}>
                            <Grid item xs={6}>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Typography component="span" variant="body2" fontWeight="bold" mr={1} mb={0}>
                                        <Translate id="hospital.billing.accountability.total_organizations" /> :
                                    </Typography>
                                    <Chip
                                        label={overallStats.totalOrganizations.toString()}
                                        color="primary"
                                        style={{ fontWeight: 'bold', backgroundColor: '#9c27b0', color: '#fff', marginRight: 8 }}
                                        size="small"
                                    />
                                </Box>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Typography component="span" variant="body2" fontWeight="bold" mr={1} mb={0}>
                                        <Translate id="hospital.billing.accountability.total_bills" /> :
                                    </Typography>
                                    <Chip
                                        label={overallStats.totalBills.toString()}
                                        color="primary"
                                        style={{ fontWeight: 'bold', backgroundColor: '#3579f8', color: '#fff', marginRight: 8 }}
                                        size="small"
                                    />
                                </Box>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Typography component="span" variant="body2" fontWeight="bold" mr={1} mb={0}>
                                        <Translate id="hospital.billing.accountability.total_billed" /> :
                                    </Typography>
                                    <Chip
                                        label={formatCurrency(overallStats.totalBilled, currency, languageCode)}
                                        color="primary"
                                        style={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: '#fff', marginRight: 8 }}
                                        size="small"
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Typography component="span" variant="body2" fontWeight="bold" mr={1} mb={0}>
                                        <Translate id="hospital.billing.accountability.total_reimbursed" /> :
                                    </Typography>
                                    <Chip
                                        label={formatCurrency(overallStats.totalReimbursed, currency, languageCode)}
                                        color="primary"
                                        style={{ fontWeight: 'bold', backgroundColor: '#4CAF50', color: '#fff', marginRight: 8 }}
                                        size="small"
                                    />
                                </Box>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Typography component="span" variant="body2" fontWeight="bold" mr={1} mb={0}>
                                        <Translate id="hospital.billing.accountability.total_pending" /> :
                                    </Typography>
                                    <Chip
                                        label={formatCurrency(overallStats.totalPending, currency, languageCode)}
                                        color="primary"
                                        style={{ fontWeight: 'bold', backgroundColor: overallStats.totalPending > 0 ? '#f44336' : '#4CAF50', color: '#fff', marginRight: 8 }}
                                        size="small"
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>
            )}

            <Grid item xs={12}>
                {renderCore()}
            </Grid>
        </Grid>
    );
};

export default AccountabilityTable;
